import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { collegesAPI, applicationsAPI } from '../utils/api';
import { Search, MapPin, Users, Plus, Check, CheckSquare, Square, Loader2, Filter, ChevronDown, ChevronUp, DollarSign, GraduationCap, Percent, BookOpen, AlertCircle, Handshake } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import api from '../utils/api';

const Colleges = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sizeFilter, setSizeFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [acceptanceFilter, setAcceptanceFilter] = useState('all');
  const [tuitionFilter, setTuitionFilter] = useState('all');
  const [testPolicy, setTestPolicy] = useState('all');
  const [majorFilter, setMajorFilter] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [colleges, setColleges] = useState([]);
  const [savedColleges, setSavedColleges] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [bulkAdding, setBulkAdding] = useState(false);
  const [showNotPartneredDialog, setShowNotPartneredDialog] = useState(false);
  const [pendingCollege, setPendingCollege] = useState(null);

  useEffect(() => {
    loadColleges();
    loadSavedColleges();
  }, []);

  useEffect(() => {
    loadColleges();
  }, [searchTerm, typeFilter, sizeFilter, locationFilter, acceptanceFilter, tuitionFilter]);

  const loadColleges = async () => {
    try {
      const filters = {};
      if (searchTerm) filters.search = searchTerm;
      if (typeFilter !== 'all') filters.type = typeFilter;
      if (sizeFilter !== 'all') filters.size = sizeFilter;
      
      let data = await collegesAPI.getAll(filters);
      
      // Apply additional client-side filters
      if (locationFilter !== 'all') {
        const regionStates = {
          'northeast': ['MA', 'CT', 'NY', 'NJ', 'PA', 'NH', 'VT', 'ME', 'RI'],
          'midwest': ['OH', 'MI', 'IN', 'IL', 'WI', 'MN', 'IA', 'MO', 'ND', 'SD', 'NE', 'KS'],
          'south': ['TX', 'FL', 'GA', 'NC', 'VA', 'TN', 'AL', 'SC', 'LA', 'KY', 'OK', 'AR', 'MS'],
          'west': ['CA', 'WA', 'OR', 'AZ', 'CO', 'NV', 'UT', 'NM', 'ID', 'MT', 'WY', 'AK', 'HI']
        };
        const states = regionStates[locationFilter] || [];
        data = data.filter(c => states.some(s => c.location?.includes(s)));
      }
      
      if (acceptanceFilter !== 'all') {
        data = data.filter(c => {
          const rate = c.acceptance_rate || 50;
          switch (acceptanceFilter) {
            case 'highly_selective': return rate < 15;
            case 'selective': return rate >= 15 && rate < 30;
            case 'moderate': return rate >= 30 && rate < 50;
            case 'less_selective': return rate >= 50;
            default: return true;
          }
        });
      }
      
      if (tuitionFilter !== 'all') {
        data = data.filter(c => {
          const tuition = c.tuition || 30000;
          switch (tuitionFilter) {
            case 'under_20k': return tuition < 20000;
            case '20k_40k': return tuition >= 20000 && tuition < 40000;
            case '40k_60k': return tuition >= 40000 && tuition < 60000;
            case 'over_60k': return tuition >= 60000;
            default: return true;
          }
        });
      }
      
      if (majorFilter) {
        data = data.filter(c => 
          c.majors?.some(m => m.toLowerCase().includes(majorFilter.toLowerCase()))
        );
      }
      
      setColleges(data);
    } catch (error) {
      console.error('Error loading colleges:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSavedColleges = async () => {
    try {
      const saved = await collegesAPI.getSaved();
      setSavedColleges(saved.map(c => c._id));
    } catch (error) {
      console.error('Error loading saved colleges:', error);
    }
  };

  const handleAddCollege = async (collegeId, confirmedNotPartnered = false) => {
    try {
      if (savedColleges.includes(collegeId)) {
        await collegesAPI.unsave(collegeId);
        setSavedColleges(savedColleges.filter(id => id !== collegeId));
        toast({
          title: 'Removed',
          description: 'College removed from your list',
        });
      } else {
        // Check if college is not partnered
        const college = colleges.find(c => c._id === collegeId);
        if (college && college.partnered === false && !confirmedNotPartnered) {
          setPendingCollege(college);
          setShowNotPartneredDialog(true);
          return;
        }
        
        await collegesAPI.save(collegeId);
        await applicationsAPI.create(collegeId);
        setSavedColleges([...savedColleges, collegeId]);
        
        // If not partnered, also notify counselor
        if (college && college.partnered === false) {
          try {
            const response = await api.post('/counselor/notify-non-partnered', {
              college_id: collegeId,
              college_name: college.name
            });
            const feeSummary = response.data?.fee_summary;
            toast({
              title: 'Added to Your List',
              description: `${college.name} has been added. A Wayo counselor will contact you. Service fee: $${feeSummary?.wayo_service_fee || 20}`,
            });
          } catch (err) {
            console.log('Counselor notification sent or endpoint not available');
            toast({
              title: 'Added to Your List',
              description: `${college.name} has been added. A Wayo professional will be assigned to assist you.`,
            });
          }
        } else {
          toast({
            title: 'Added',
            description: 'College added to your list',
          });
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const confirmAddNotPartnered = async () => {
    if (pendingCollege) {
      setShowNotPartneredDialog(false);
      await handleAddCollege(pendingCollege._id, true);
      setPendingCollege(null);
    }
  };

  const toggleSelectCollege = (collegeId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(collegeId)) {
      newSelected.delete(collegeId);
    } else {
      newSelected.add(collegeId);
    }
    setSelectedIds(newSelected);
  };

  const selectAllVisible = () => {
    const notSavedColleges = colleges.filter(c => !savedColleges.includes(c._id));
    setSelectedIds(new Set(notSavedColleges.map(c => c._id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleBulkAdd = async () => {
    if (selectedIds.size === 0) {
      toast({
        title: 'No colleges selected',
        description: 'Please select colleges to add',
        variant: 'destructive',
      });
      return;
    }

    setBulkAdding(true);
    let added = 0;
    let failed = 0;

    for (const collegeId of selectedIds) {
      if (!savedColleges.includes(collegeId)) {
        try {
          await collegesAPI.save(collegeId);
          await applicationsAPI.create(collegeId);
          added++;
        } catch (error) {
          failed++;
        }
      }
    }

    // Refresh saved colleges
    await loadSavedColleges();
    setSelectedIds(new Set());
    setBulkAdding(false);

    toast({
      title: 'Bulk Add Complete',
      description: `Added ${added} colleges${failed > 0 ? `, ${failed} failed` : ''}`,
    });
  };

  const notSavedCount = colleges.filter(c => !savedColleges.includes(c._id)).length;
  const selectedCount = selectedIds.size;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Search Colleges</h1>
          <p className="text-gray-600">Explore and add colleges to your application list</p>
        </div>

        {/* Search and Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            {/* Basic Filters Row */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
              <div className="md:col-span-2 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search colleges by name or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Private">Private</SelectItem>
                  <SelectItem value="Public">Public</SelectItem>
                </SelectContent>
              </Select>
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  <SelectItem value="northeast">Northeast</SelectItem>
                  <SelectItem value="midwest">Midwest</SelectItem>
                  <SelectItem value="south">South</SelectItem>
                  <SelectItem value="west">West</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant="outline"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2"
              >
                <Filter className="h-4 w-4" />
                More Filters
                {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>

            {/* Advanced Filters */}
            {showAdvanced && (
              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Advanced Filters</p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                      <Percent className="h-3 w-3" /> Acceptance Rate
                    </label>
                    <Select value={acceptanceFilter} onValueChange={setAcceptanceFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any Acceptance Rate</SelectItem>
                        <SelectItem value="highly_selective">Highly Selective (&lt;15%)</SelectItem>
                        <SelectItem value="selective">Selective (15-30%)</SelectItem>
                        <SelectItem value="moderate">Moderate (30-50%)</SelectItem>
                        <SelectItem value="less_selective">Less Selective (50%+)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                      <DollarSign className="h-3 w-3" /> Tuition Range
                    </label>
                    <Select value={tuitionFilter} onValueChange={setTuitionFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any Tuition</SelectItem>
                        <SelectItem value="under_20k">Under $20,000</SelectItem>
                        <SelectItem value="20k_40k">$20,000 - $40,000</SelectItem>
                        <SelectItem value="40k_60k">$40,000 - $60,000</SelectItem>
                        <SelectItem value="over_60k">Over $60,000</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                      <Users className="h-3 w-3" /> Campus Size
                    </label>
                    <Select value={sizeFilter} onValueChange={setSizeFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any Size</SelectItem>
                        <SelectItem value="Small">Small (&lt;5,000)</SelectItem>
                        <SelectItem value="Medium">Medium (5,000-15,000)</SelectItem>
                        <SelectItem value="Large">Large (15,000+)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                      <BookOpen className="h-3 w-3" /> Major
                    </label>
                    <Input
                      placeholder="e.g., Computer Science"
                      value={majorFilter}
                      onChange={(e) => setMajorFilter(e.target.value)}
                    />
                  </div>
                </div>
                
                {/* Clear Filters */}
                <div className="flex justify-end mt-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setTypeFilter('all');
                      setLocationFilter('all');
                      setAcceptanceFilter('all');
                      setTuitionFilter('all');
                      setSizeFilter('all');
                      setMajorFilter('');
                      setSearchTerm('');
                    }}
                  >
                    Clear All Filters
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bulk Actions Bar */}
        <Card className="mb-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={selectedCount > 0 && selectedCount === notSavedCount}
                    onCheckedChange={(checked) => checked ? selectAllVisible() : deselectAll()}
                    className="border-white data-[state=checked]:bg-white data-[state=checked]:text-emerald-600"
                  />
                  <span className="font-medium">
                    {selectedCount > 0 ? `${selectedCount} selected` : 'Select colleges'}
                  </span>
                </div>
                <Button 
                  variant="secondary" 
                  size="sm"
                  onClick={selectAllVisible}
                  className="bg-white/20 hover:bg-white/30 text-white border-0"
                >
                  <CheckSquare className="h-4 w-4 mr-1" />
                  Select All ({notSavedCount})
                </Button>
                {selectedCount > 0 && (
                  <Button 
                    variant="secondary" 
                    size="sm"
                    onClick={deselectAll}
                    className="bg-white/20 hover:bg-white/30 text-white border-0"
                  >
                    <Square className="h-4 w-4 mr-1" />
                    Clear Selection
                  </Button>
                )}
              </div>
              <Button 
                onClick={handleBulkAdd}
                disabled={selectedCount === 0 || bulkAdding}
                className="bg-white text-emerald-600 hover:bg-gray-100"
              >
                {bulkAdding ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding...</>
                ) : (
                  <><Plus className="h-4 w-4 mr-2" /> Add {selectedCount} Colleges</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* College List */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-600">
                Showing {colleges.length} colleges • {savedColleges.length} already added
              </p>
            </div>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : colleges.length === 0 ? (
              <div className="text-center py-8">No colleges found</div>
            ) : (
              colleges.map(college => {
                const isSaved = savedColleges.includes(college._id);
                const isSelected = selectedIds.has(college._id);
                const isPartnered = college.partnered !== false;
                
                return (
                  <Card
                    key={college._id}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      selectedCollege?._id === college._id ? 'ring-2 ring-emerald-500' : ''
                    } ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''} ${isSaved ? 'bg-green-50' : ''}`}
                    onClick={() => setSelectedCollege(college)}
                  >
                    <CardContent className="p-6">
                      <div className="flex gap-4">
                        {/* Checkbox */}
                        <div className="flex items-start pt-1" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            disabled={isSaved}
                            onCheckedChange={() => toggleSelectCollege(college._id)}
                          />
                        </div>
                        <img
                          src={college.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(college.name)}&size=96&background=10b981&color=fff`}
                          alt={college.name}
                          className="w-24 h-24 rounded-lg object-cover"
                        />
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-xl font-semibold">{college.name}</h3>
                                {!isPartnered && (
                                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-xs">
                                    Not Partnered
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center text-sm text-gray-600 mb-2">
                                <MapPin className="h-4 w-4 mr-1" />
                                {college.location}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant={isSaved ? 'default' : 'outline'}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddCollege(college._id);
                              }}
                              className={isSaved ? 'bg-green-600 hover:bg-green-700' : ''}
                            >
                              {isSaved ? (
                                <><Check className="h-4 w-4 mr-1" /> Added</>
                              ) : (
                                <><Plus className="h-4 w-4 mr-1" /> Add</>
                              )}
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-2 mb-2">
                            <Badge variant="secondary">{college.type}</Badge>
                            {college.ranking && <Badge variant="outline">Rank #{college.ranking}</Badge>}
                            <Badge variant="outline">{college.acceptance_rate}% acceptance</Badge>
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-2">{college.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>

          {/* College Details Sidebar */}
          <div className="lg:sticky lg:top-20 h-fit">
            {selectedCollege ? (
              <Card>
                <CardHeader>
                  <img
                    src={selectedCollege.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedCollege.name)}&size=400&background=10b981&color=fff`}
                    alt={selectedCollege.name}
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                  <CardTitle>{selectedCollege.name}</CardTitle>
                  <CardDescription>{selectedCollege.location}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">About</h4>
                    <p className="text-sm text-gray-600">{selectedCollege.description}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Type</p>
                      <p className="font-semibold">{selectedCollege.type}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Ranking</p>
                      <p className="font-semibold">#{selectedCollege.ranking || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Acceptance Rate</p>
                      <p className="font-semibold">{selectedCollege.acceptance_rate}%</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Tuition</p>
                      <p className="font-semibold">${selectedCollege.tuition?.toLocaleString() || 'N/A'}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Application Deadline</h4>
                    <p className="text-sm text-gray-600">
                      {selectedCollege.deadline ? new Date(selectedCollege.deadline).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      }) : 'Contact school'}
                    </p>
                  </div>

                  {selectedCollege.requirements && (
                    <div>
                      <h4 className="font-semibold mb-2">Requirements</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {selectedCollege.requirements.map((req, index) => (
                          <li key={index} className="flex items-start">
                            <span className="mr-2">•</span>
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedCollege.majors && (
                    <div>
                      <h4 className="font-semibold mb-2">Popular Majors</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedCollege.majors.slice(0, 8).map((major, index) => (
                          <Badge key={index} variant="outline">{major}</Badge>
                        ))}
                        {selectedCollege.majors.length > 8 && (
                          <Badge variant="secondary">+{selectedCollege.majors.length - 8} more</Badge>
                        )}
                      </div>
                    </div>
                  )}

                  <Button
                    className="w-full"
                    onClick={() => handleAddCollege(selectedCollege._id)}
                  >
                    {savedColleges.includes(selectedCollege._id) ? (
                      <><Check className="h-4 w-4 mr-2" /> Added to My Colleges</>
                    ) : (
                      <><Plus className="h-4 w-4 mr-2" /> Add to My Colleges</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="pt-6 text-center">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">Select a college to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Not Partnered Dialog */}
      <Dialog open={showNotPartneredDialog} onOpenChange={setShowNotPartneredDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Handshake className="h-5 w-5 text-amber-600" />
              University Not Yet Partnered
            </DialogTitle>
            <DialogDescription className="pt-4">
              <div className="space-y-4">
                <p>
                  <strong>{pendingCollege?.name}</strong> has not yet partnered with Wayo.
                </p>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <p className="text-emerald-800 font-medium mb-2">What happens next:</p>
                  <ul className="text-sm text-emerald-700 space-y-2">
                    <li className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      Your profile will be shared with a Wayo professional
                    </li>
                    <li className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      Our team will work <strong>one-on-one</strong> with you and the university
                    </li>
                    <li className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      We'll guide you through the entire application process
                    </li>
                    <li className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      You'll receive an email with fee details and next steps
                    </li>
                  </ul>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    <strong>Fee Summary:</strong><br/>
                    • University Application Fee: Varies by institution<br/>
                    • Wayo One-on-One Assistance: <strong>$20</strong> (flat fee)
                  </p>
                </div>
                <p className="text-sm text-gray-600">
                  Since this university hasn't partnered with us yet, a dedicated Wayo counselor will assist you personally to make your application successful.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowNotPartneredDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmAddNotPartnered} className="bg-emerald-600 hover:bg-emerald-700">
              Add University
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Colleges;
